import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import CallData from "../classes/CallData";
import Button from "./Button";
import TwilioClient from "../classes/TwilioClient";
import Lead from "../models/Lead";
import User from "../models/User";
import TextView from "./TextView";
import PageNavigator from "../classes/PageNavigator";
import LeadOverviewWidget from "./LeadOverviewWidget";
import { BuildContext } from "../classes/BuildContext";

type _BackButtonOnPressed = (d3eState: LeadOverviewPageRefs) => void;

export interface LeadOverviewPageProps extends BaseUIProps {
  key?: string;
  lead: Lead;
  user: User;
  client: TwilioClient;
  data: CallData;
}
/// To store state data for LeadOverviewPage
class LeadOverviewPageRefs {
  public backButton: BackButtonState = new BackButtonState();
}

interface BackButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: LeadOverviewPageRefs;
  _backButtonHandler?: _BackButtonOnPressed;
}

class BackButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _BackButtonWithState extends ObservableComponent<BackButtonWithStateProps> {
  backButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  public constructor(props: BackButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get backButton(): BackButtonState {
    return this.props.d3eState.backButton;
  }
  public get d3eState(): LeadOverviewPageRefs {
    return this.props.d3eState;
  }
  public get _backButtonHandler(): _BackButtonOnPressed {
    return this.props._backButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("backButton", null, this.backButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["backButton"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    return Button({
      disable: this.backButton.disable,
      onPressed: () => {
        this._backButtonHandler(this.d3eState);
      },
      onFocusChange: (val) => {},
      child: TextView({ data: "< Go Back", className: "vc" }),
      className: "vc",
    });
  }
}
function BackButtonWithState(props: BackButtonWithStateProps) {
  return React.createElement(_BackButtonWithState, props);
}

class _LeadOverviewPageState extends ObservableComponent<LeadOverviewPageProps> {
  static defaultProps = { lead: null, user: null, client: null, data: null };
  d3eState: LeadOverviewPageRefs = new LeadOverviewPageRefs();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: LeadOverviewPageProps) {
    super(props);

    this.initState();
  }
  public get lead(): Lead {
    return this.props.lead;
  }
  public get user(): User {
    return this.props.user;
  }
  public get client(): TwilioClient {
    return this.props.client;
  }
  public get data(): CallData {
    return this.props.data;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("lead", this.props.lead);

    this.updateSyncProperty("user", this.props.user);

    this.updateSyncProperty("data", this.props.data);

    this.on(["client", "data", "lead", "user"], this.rebuild);
  }
  public componentDidUpdate(prevProps: LeadOverviewPageProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.lead !== this.props.lead) {
      this.updateObservable("lead", prevProps.lead, this.props.lead);

      this.fire("lead", this);
    }

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }

    if (prevProps.client !== this.props.client) {
      this.fire("client", this);
    }

    if (prevProps.data !== this.props.data) {
      this.updateObservable("data", prevProps.data, this.props.data);

      this.fire("data", this);
    }
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      children: [
        ui.Row({
          children: [
            BackButtonWithState({
              d3eState: this.d3eState,
              _backButtonHandler: this.backButtonHandler,
              key: "0",
            }),
          ],
          className: "x8df hc vc h",
          key: "0",
        }),
        ui.Row({
          children: [ui.Container({ className: "x9aa hc vc h", key: "0" })],
          className: "xadc hc h",
          key: "1",
        }),
        ui.Row({
          children: [
            ui.Container({
              expand: true,
              child: LeadOverviewWidget({
                lead: this.lead,
                user: this.user,
                client: this.client,
                data: this.data,
              }),
              key: "0",
              className: "xaac hc vc h v",
            }),
          ],
          className: "x2f94 hc vc h v",
          key: "2",
        }),
      ],
      className: ui.join("LeadOverviewPage hc vc ", this.props.className),
      ...copyBaseUIProps(this.props),
    });
  }
  public backButtonHandler = (d3eState: LeadOverviewPageRefs): void => {
    this.navigator.pop();
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get backButton() {
    return this.d3eState.backButton;
  }
}
export default function LeadOverviewPage(props: LeadOverviewPageProps) {
  return React.createElement(_LeadOverviewPageState, {
    ..._LeadOverviewPageState.defaultProps,
    ...props,
  });
}
